import { api } from "./index";

export type ProjectSummarySheetStatus =
  | "DRAFT"
  | "UNDER_PEER_REVIEW"
  | "UNDER_HOP_REVIEW"
  | "APPROVED"
  | "REJECTED";

export interface BudgetAllocationDto {
  budgetYear: number;
  allocatedAmount: number;
  sortOrder?: number;
}

export interface ProjectSummarySheet {
  id: string;
  docId: string;
  summarySheetNumber: string;
  sheetDate: string;
  projectNumber?: string | null;
  projectName: string;
  fundingAgency?: string | null;
  scopeOfWorkDetail?: string | null;
  estimatedCost?: number | null;
  numberOfTimesAnnounced?: number | null;
  dateAnnounced?: string | null;
  numberOfEoi?: number | null;
  numberOfBidsSubmitted?: number | null;
  submittedParty?: string | null;
  detailsOfTheProject?: string | null;
  bidCommitteeReference?: string | null;
  agendaReference?: string | null;
  awardedParty?: string | null;
  proposedPrice?: number | null;
  proposedDuration?: string | null;
  tenderId?: string | null;
  submittedById: string;
  status: ProjectSummarySheetStatus;
  createdAt: string;
  updatedAt: string;
  doc: { id: string; fileName: string; bucket: string; objectKey: string };
  author?: { id: string; fullName: string; fullNameDhivehi?: string | null; email: string; role: string };
  tender?: { id: string; referenceNo: string; description: string } | null;
  budgetAllocations: Array<{
    id: string;
    sheetId: string;
    budgetYear: number;
    allocatedAmount: number;
    sortOrder: number;
  }>;
  approvals?: Array<{
    id: string;
    stage: "PEER_CHECK" | "HEAD_OF_PROCUREMENT";
    status: "PENDING" | "APPROVED" | "REJECTED";
    comments?: string | null;
    approvedAt?: string | null;
    rejectedAt?: string | null;
    approver: { id: string; fullName: string; fullNameDhivehi?: string | null; email: string; role: string };
  }>;
}

export interface CreateProjectSummarySheetDto {
  summarySheetNumber?: string;
  sheetDate: string;
  projectNumber?: string | null;
  projectName: string;
  fundingAgency?: string | null;
  scopeOfWorkDetail?: string | null;
  estimatedCost?: number | null;
  numberOfTimesAnnounced?: number | null;
  dateAnnounced?: string | null;
  numberOfEoi?: number | null;
  numberOfBidsSubmitted?: number | null;
  submittedParty?: string | null;
  detailsOfTheProject?: string | null;
  bidCommitteeReference?: string | null;
  agendaReference?: string | null;
  awardedParty?: string | null;
  proposedPrice?: number | null;
  proposedDuration?: string | null;
  tenderId?: string | null;
  budgetAllocations: BudgetAllocationDto[];
  fileName?: string;
  base64File?: string;
}

export type UpdateProjectSummarySheetDto = Partial<CreateProjectSummarySheetDto>;

export interface ProjectSummarySheetListResponse {
  sheets: ProjectSummarySheet[];
  pagination: { page: number; limit: number; total: number; pages: number };
}

export const projectSummarySheetApi = {
  getNextSheetNumber: async (year?: number): Promise<{ summarySheetNumber: string }> => {
    const response = await api.get<{ summarySheetNumber: string }>(
      "/project-summary-sheets/next-number",
      { params: year != null ? { year } : undefined }
    );
    return response.data;
  },

  list: (params?: { status?: string; page?: number; limit?: number }) =>
    api.get<ProjectSummarySheetListResponse>("/project-summary-sheets", { params }),
  getById: (id: string) => api.get<ProjectSummarySheet>(`/project-summary-sheets/${id}`),
  create: (data: CreateProjectSummarySheetDto) =>
    api.post<ProjectSummarySheet>("/project-summary-sheets", data),
  update: (id: string, data: UpdateProjectSummarySheetDto) =>
    api.put<ProjectSummarySheet>(`/project-summary-sheets/${id}`, data),
  delete: (id: string) => api.delete<{ message: string }>(`/project-summary-sheets/${id}`),
  download: (id: string) => api.get(`/project-summary-sheets/${id}/download`, { responseType: "blob" }),
  generatePdf: (id: string) => api.get(`/project-summary-sheets/${id}/pdf`, { responseType: "blob" }),
  submit: (id: string, data: { checkerId: string }) =>
    api.post<ProjectSummarySheet>(`/project-summary-sheets/${id}/submit`, data),
  approvePeer: (id: string, data?: { comments?: string }) =>
    api.post<ProjectSummarySheet>(`/project-summary-sheets/${id}/approve-peer`, data ?? {}),
  approveHead: (id: string, data?: { comments?: string }) =>
    api.post<ProjectSummarySheet>(`/project-summary-sheets/${id}/approve`, data ?? {}),
  reject: (id: string, data: { comments: string }) =>
    api.post<ProjectSummarySheet>(`/project-summary-sheets/${id}/reject`, data),
  getPendingApprovals: () => api.get<ProjectSummarySheetWithPending[]>("/project-summary-sheets/approvals/pending"),
};

export type ProjectSummarySheetWithPending = ProjectSummarySheet & {
  approval: {
    id: string;
    stage: string;
    status: string;
    approver: { id: string; fullName: string; fullNameDhivehi?: string | null; email: string; role: string };
  };
};
